import RTPaaS from './RTPaaS.js';
import Constants from './Constants.js';
import Payload from './Payload.js';
import ProctorKendoWindow from '../widgets/ProctorKendoWindow';
import DockManager from '../widgets/DockManager.js';
import Broadcast from './Broadcast.js';
import CaptureImage from "./CaptureImage";
import CustomKendoWindow from "../widgets/CustomKendoWindow";
import AudioVideoClient from "./AudioVideoClient";
export default class RTPaaSProctor extends RTPaaS {

	constructor(idWithNameJson, ablyOptions, config) {
		super(config, ablyOptions);

		//Don't finish initialization until after ably is connected.
		const ablyConnection = this.establishAblyConnection();
		this.coordChannelId = null;
		this.sharedState = {
			monitoringStarted: false,
			meetingId: null,
			initialGroupCallDone: false
		};
		if (ablyConnection){
			ablyConnection.then(async () => {
				// Initialize properties and configurations
				this.clientId = this.ably.auth.clientId;
				// If a clientId is provided in the config, use it as the proctorAttendeeId. Otherwise, generate a random UUID.
				this.proctorAttendeeId = this.config.clientId ? this.config.clientId : crypto.randomUUID();
				this.idAndNameMap = new Map(Object.entries(idWithNameJson));
				this.testersInCall = new Set();
				this.config.streamingConfigs = {
					...this.getDefaultMonitoringConfigs(),
					...this.config?.streamingConfigs
				};

				this.unseenMessagesAry = [];

				// Initialize and attach listeners
				await this.init();
				this.onConnection();
				this.attachListeners();
				config.initCallback?.();
			}).catch((err) => {
				console.error(`RTPaaSProctor: An error occurred during initialization: ${err}`);
			});
		} else {
			console.error(`RTPaaSProctor: An error occurred during Ably connection.`);
		}
	}

	async init() {
		this.kendoWindows = new Map();
		this.testerImageStatuses = new Map();
		this.testerConnectionStatuses = new Map();
		this.testerStatusImages = new Map();
		this.testerHandStatuses = new Map();
		this.testerLockStatuses = new Map();
		this.imageTimeouts = new Map();
		this.dockManager = new DockManager(this);
		this.isInVideoCall = false;
		this.isInGroupAudioCall = false;
		this.isRestartingVideoMonitoringInProgress = false;
		this.videoMonitoringMeetingId = undefined;
		this.createDisplayName(this.idAndNameMap);
		this.setupAblyChannels(this.idAndNameMap.keys());
		this.disconnectTimeouts = new Map();
		this.suspendTimeouts = new Map();
		this.wasInitiallyReconnected = false;
		this.idAndNameMap.forEach((_, testerIdentifier) => {
			const initialLockStatus = Boolean(this.config?.groupCallFirst && this.config?.unlockByIdHandler !== undefined);
			this.testerLockStatuses.set(testerIdentifier, initialLockStatus);
			this.testerImageStatuses.set(testerIdentifier, Constants.TesterImageStatus.NOT_JOINED);
			this.testerConnectionStatuses.set(testerIdentifier, Constants.TesterVideoConnectionToSession.PENDING);
			if (this.config?.streamingConfigs.monitoringType === Constants.MonitoringType.BY_IMAGE) {
				this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("videoMonitoring:videoWaiting"));
			} else {
				this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("generalWords:pleaseWait"), rtp_i18next.t("generalWords:loading"));
			}
		});

		if (this.config?.streamingConfigs.monitoringType === Constants.MonitoringType.BY_IMAGE) {
			this.populateStatusImages();
		} else {
			// Monitoring by video is the default behavior.
			// Pre-meeting coordination: check if another proctor already has a meeting
			const coordinatedMeetingId = await this.getOrCreateMeetingId();

			let { meetingId, wasReconnected } = await this.startVideoMonitoring(false, coordinatedMeetingId);
			this.wasInitiallyReconnected = wasReconnected || Boolean(coordinatedMeetingId);

			// Publish meetingId to session coordination channel and handle conflicts
			if (meetingId && !coordinatedMeetingId) {
				const { conflict, winningMeetingId } = await this.publishMeetingToSession(meetingId);
				if (conflict) {
					await this.monitoringAudioVideoClient.cleanup();
					const result = await this.startVideoMonitoring(false, winningMeetingId);
					meetingId = result.meetingId;
					this.wasInitiallyReconnected = true;
				}
			}

			console.log("RTPaaSProctor: Previous Video monitoring ID:", coordinatedMeetingId);
			console.log("RTPaaSProctor: Video monitoring started with meeting ID:", meetingId);

			if (meetingId) {
				this.videoMonitoringMeetingId = meetingId;

				this.coordChannelId = `${Constants.Prefixes.RTP_PROCTORS_COORDINATION_CHANNEL_PREFIX + '_' + this.config.proctorChannelId}`;
				const channel = await this.createChannelWithHistory(this.coordChannelId);

				// Initialize proctor coordination channel once
				if(wasReconnected === true) {
					const updatedSharedState = await this.getSharedStateFromCoordinationChannel(channel);
					this.setSharedState(updatedSharedState);
					await this.publishStateToPresence(false);
				} else {
					this.setSharedState({
						isProctor: true,
						meetingId: this.videoMonitoringMeetingId,
						monitoringStarted: true,
						initialGroupCallDone: false
					});

					await this.publishStateToPresence(true);
				}

				// This message also helps to connect to the testers if the proctor was disconnected.
				for (const [testerIdentifier] of this.idAndNameMap) {
					// If the proctor was reconnected, update the tester's video immediately.
					if (this.wasInitiallyReconnected) {
						await this.updateTesterStatusInRefresh(testerIdentifier);
					} else {
						this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("videoMonitoring:videoWaiting"));
						// If this is the initial connection, send the meeting ID to the tester.
						const messageContents = {
							meetingId: this.videoMonitoringMeetingId,
							videoQuality: this.config.streamingConfigs.videoQuality,
							videoMonitoringStatus: Constants.TesterVideoConnectionToSession.PENDING
						};
						this.publishMessage(
							testerIdentifier,
							RTPaaS.createMessage(
								Payload.createPayload(
									this.clientId,
									Constants.Role.PROCTOR,
									testerIdentifier,
									Constants.MessageNames.CALL,
									Constants.EventTypes.VIDEO_MONITORING_CALL,
									messageContents
								)
							)
						);
					}
				}
			}else {
				console.error('RTPaaSProctor: Failed to start video monitoring call. Meeting ID was not returned.');
			}
		}

		document.addEventListener("visibilitychange", () => {
			if (document.visibilityState === "visible") {
				for (let testerIdentifier of this.kendoWindows.keys()) {
					let proctorWindow = this.kendoWindows.get(testerIdentifier);
					if (proctorWindow && proctorWindow.unseenChatCount <= 0) {
						this.sendUnseenReceivedChatMessage(testerIdentifier);
					}
				}
			}
		});
	}

	getSessionCoordinationChannelId() {
		if (!this.config?.proctorChannelId) {
			console.warn('RTPaaSProctor: No proctorChannelId in config — pre-meeting coordination disabled');
			return null;
		}
		return `${Constants.Prefixes.RTP_PROCTORS_COORDINATION_CHANNEL_PREFIX}session_${this.config.proctorChannelId}`;
	}

	async getOrCreateMeetingId() {
		try {
			const channelId = this.getSessionCoordinationChannelId();
			if (!channelId) return null;

			const channel = this.getChannelWithHistory(channelId);

			// Timeout presence operations so a hanging channel doesn't block init
			const COORDINATION_TIMEOUT_MS = 15000;
			const timeout = (ms) => new Promise((_, reject) =>
				setTimeout(() => reject(new Error(`Coordination timed out after ${ms}ms`)), ms)
			);

			const members = await Promise.race([
				channel.presence.get(),
				timeout(COORDINATION_TIMEOUT_MS)
			]);
			const now = Date.now();

			//If the session goes beyond the max meeting age, we can consider it stale and allow a new meeting to be created.
			//This can cause problems if a proctor wants to join an existing meeting after the ma age.
			const existingMeeting = members.find(m =>
				m.data?.meetingId &&
				m.data?.lastUpdated &&
				(now - m.data.lastUpdated) <= Constants.VideoCapture.MAX_MEETING_AGE_MS
			);
			if (existingMeeting) {
				await Promise.race([
					channel.presence.enter({
						clientId: this.clientId,
						meetingId: existingMeeting.data.meetingId,
						lastUpdated: Date.now()
					}),
					timeout(COORDINATION_TIMEOUT_MS)
				]);
				return existingMeeting.data.meetingId;
			} else {
				let pastMeeting = null;
				await Promise.race([
				channel.presence.history().then(history => {
					pastMeeting = history.items.reverse().find(item =>
						item.data?.meetingId &&
						item.data?.lastUpdated &&
						(now - item.data.lastUpdated) <= Constants.VideoCapture.MAX_MEETING_AGE_MS
					);
				}),
				timeout(COORDINATION_TIMEOUT_MS)
				]);
				if (pastMeeting) {
					return pastMeeting.data.meetingId;
				}
			}

			// No existing meeting, so enter presence to create a new one with a meetingId that will be published later
			await Promise.race([
				channel.presence.enter({
					clientId: this.clientId,
					lastUpdated: Date.now()
				}),
				timeout(COORDINATION_TIMEOUT_MS)
			]);

			return null;
		} catch (error) {
			console.error('Error in pre-meeting coordination:', error);
			return null;
		}
	}

	async publishMeetingToSession(meetingId) {
		try {
			const channelId = this.getSessionCoordinationChannelId();
			if (!channelId) return { conflict: false };

			const channel = this.getChannelWithHistory(channelId);

			const COORDINATION_TIMEOUT_MS = 15000;
			const timeout = (ms) => new Promise((_, reject) =>
				setTimeout(() => reject(new Error(`Coordination timed out after ${ms}ms`)), ms)
			);

			await Promise.race([
				channel.presence.update({
					clientId: this.clientId,
					meetingId: meetingId,
					lastUpdated: Date.now()
				}),
				timeout(COORDINATION_TIMEOUT_MS)
			]);

			const members = await Promise.race([
				channel.presence.get(),
				timeout(COORDINATION_TIMEOUT_MS)
			]);
			const meetingIds = [...new Set(
				members
					.filter(m => m.data?.meetingId)
					.map(m => m.data.meetingId)
			)];

			if (meetingIds.length > 1) {
				meetingIds.sort();
				const winningMeetingId = meetingIds[0];
				return {
					conflict: meetingId !== winningMeetingId,
					winningMeetingId
				};
			}

			return { conflict: false };
		} catch (error) {
			console.error('Error publishing meeting to session:', error);
			return { conflict: false };
		}
	}

	getSharedState() {
		return this.sharedState;
	}

	setSharedState(newState) {
		this.sharedState = {
			...this.sharedState,
			...newState,
		};
	}

	// Store state in presence when monitoring starts or changes
	async publishStateToPresence(enter) {
		if (!this.coordChannelId) return;
		const targetChannel = this.getChannelWithHistory(this.coordChannelId);
		const sharedState = this.getSharedState();
		const presenceData = {
			isProctor: true,
			meetingId: sharedState.meetingId,
			monitoringStarted: sharedState.monitoringStarted,
			initialGroupCallDone: sharedState.initialGroupCallDone,
			lastUpdated: Date.now()
		}
		if (enter === true){
			await targetChannel.presence.enter(presenceData);
		} else {
			await targetChannel.presence.update(presenceData);
		}
	}

	// Retrieve merged state from all present proctors
	async getSharedStateFromCoordinationChannel(channel) {
		try {
			if (!this.coordChannelId) return this.getSharedState();
			channel = channel || this.getChannelWithHistory(this.coordChannelId);
			const members = await channel?.presence.get();

			// Get the most recent state that has data with meetingId, monitoringStarted, and initialGroupCallDone
			let latestStateMember = null;
			for (const member of members) {
				if (!member?.data?.meetingId || member.data.monitoringStarted === undefined) {
					continue;
				}
				if (!latestStateMember || member.data.lastUpdated > latestStateMember.data.lastUpdated) {
					latestStateMember = member;
				}
			}

			const data = latestStateMember?.data;
			if (data) {
				return {
					monitoringStarted: data.monitoringStarted,
					meetingId: data.meetingId,
					initialGroupCallDone: data.initialGroupCallDone
				};
			}
			return this.getSharedState();
		} catch (error) {
			console.error('Error retrieving shared state from coordination channel:', error);
			return this.getSharedState();
		}
	}

	 async findTesterVideoMonitoringCurrentStatus(testerIdentifier) {
		const history = await this.retrieveHistory(testerIdentifier, Constants.RetrieveHistory.DEFAULT_LIMIT_MINUTE);
		const currentStatus = history.filter(msg => msg.data.videoMonitoringStatus !== undefined && msg.data.testerIdentifier === testerIdentifier).sort((a, b) => b.timestamp - a.timestamp);
		if (currentStatus.length > 0) {
			return currentStatus[0].data?.videoMonitoringStatus;
		} else {
			return Constants.TesterVideoConnectionToSession.PENDING;
		}
	}

	// Method to handle the current status of the attendee in a refreshed proctor session
	async updateTesterStatusInRefresh(testerIdentifier) {
		 const isTesterPresent = await this.isTesterPresent(this.getChannelWithHistory(testerIdentifier));
		 const testerLastVideoMonitoringStatus = await this.findTesterVideoMonitoringCurrentStatus(testerIdentifier);
		 testerLastVideoMonitoringStatus && this.updateTesterImageBasedOnStatus(testerIdentifier, testerLastVideoMonitoringStatus);
		 this.testerConnectionStatuses.set(testerIdentifier, testerLastVideoMonitoringStatus);
		 if (isTesterPresent) {
			 if (testerLastVideoMonitoringStatus === Constants.TesterVideoConnectionToSession.CONNECTED) {
				 // If the tester is present and the last status is connected, enable video monitoring after a short delay
				 setTimeout(() => {
					 this.toggleVideoMonitoring(testerIdentifier, true);
				 }, 100);
			 } else if (testerLastVideoMonitoringStatus === Constants.TesterVideoConnectionToSession.SUSPENDED){
				 this.attendeeSuspendedVideo(testerIdentifier);
			 }
		 }
	}

	updateTesterImageBasedOnStatus(testerIdentifier, status) {
		switch (status) {
			case Constants.TesterVideoConnectionToSession.CONNECTED:
				this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("generalWords:callConnected"));
				break;
			case Constants.TesterVideoConnectionToSession.CONNECTING:
				this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("videoMonitoring:videoReconnecting"), rtp_i18next.t("generalWords:loading"));
				break;
			case Constants.TesterVideoConnectionToSession.UNSTABLE:
				this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("videoMonitoring:weakConnection"), rtp_i18next.t("videoMonitoring:noVideoAvailable"));
				break;
			case Constants.TesterVideoConnectionToSession.SUSPENDED:
				this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("proctorWidget:cameraNotWorking"), rtp_i18next.t("videoMonitoring:noVideoAvailable"));
				break;
			case Constants.TesterVideoConnectionToSession.DISCONNECTED:
				this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("proctorWidget:testerExited"), rtp_i18next.t("videoMonitoring:noVideoAvailable"));
				break;
			case Constants.TesterVideoConnectionToSession.PENDING:
				this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("videoMonitoring:videoWaiting"));
				break;
		}
	}


	//@TODO: Remove it after PAN is deployed
	// Retrieve previous meeting ID from Ably history
	async retrievePreviousMeetingId(testerIds) {
	  testerIds = testerIds.toArray();

	  try {
		for (const id of testerIds) {
		  const channel = this.createOrGetChannel(`${Constants.Prefixes.RTP_CHANNEL_PREFIX}${id}`);

		  // Remove time constraint - search all recent history
		  let hasNext = true;
		  let paginatedResult = await channel.history({
			'direction': 'backwards',
			'limit': 50
		  });

		  while (hasNext) {
			for (const item of paginatedResult.items) {
			  const message = item.data;
			  if (message.senderType === Constants.Role.PROCTOR &&
				  message.eventType === Constants.EventTypes.VIDEO_MONITORING_CALL &&
				  message.meetingId) {
				  // Check if the message is recent enough
				  const messageAge = Date.now() - item.timestamp;
				  if (messageAge <= Constants.VideoCapture.MAX_MEETING_AGE_MS) {
					  return message.meetingId;
				  }
			  }
			}

			hasNext = paginatedResult.hasNext();
			if (hasNext) {
			  paginatedResult = await paginatedResult.next();
			}
		  }
		}

		return null;
	  } catch (error) {
		console.error('Error retrieving previous meeting ID:', error);
		return null;
	  }
	}

	// Callback when a tester disconnects from the video monitoring call
	attendeeDisconnectedCallback(testerIdentifier) {
		if (this.isIdentifierProctor(testerIdentifier) ||
			!this.idAndNameMap.has(testerIdentifier)) {
			return;
		}

		if (this.testerConnectionStatuses.get(testerIdentifier) === Constants.TesterVideoConnectionToSession.DISCONNECTED ||
			this.testerConnectionStatuses.get(testerIdentifier) === Constants.TesterVideoConnectionToSession.SUSPENDED ||
			this.isRestartingVideoMonitoringInProgress === true
		){
			return;
		}

		this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("videoMonitoring:weakConnection"), rtp_i18next.t("videoMonitoring:noVideoAvailable"));
		this.testerConnectionStatuses.set(testerIdentifier, Constants.TesterVideoConnectionToSession.UNSTABLE);

		// To update the tester's video monitoring status in history
		const messageContents = {
			videoMonitoringStatus: Constants.TesterVideoConnectionToSession.UNSTABLE
		};
		this.publishMessage(
			testerIdentifier,
			RTPaaS.createMessage(
				Payload.createPayload(
					this.clientId,
					Constants.Role.PROCTOR,
					testerIdentifier,
					Constants.MessageNames.CALL,
					Constants.EventTypes.VIDEO_MONITORING_STATUS_UPDATE,
					messageContents
				)
			)
		);

		if (!this.disconnectTimeouts.has(testerIdentifier)) {
			this.disconnectTimeouts.set(testerIdentifier, setTimeout(() => {
				if (this.testerConnectionStatuses.get(testerIdentifier) === Constants.TesterVideoConnectionToSession.UNSTABLE) {
					//To prevent mass disconnect when chime is having issues, when new attendees are added or refresh
					console.log("A force exit was requested by ", testerIdentifier);
					//this.forceExitTesterFromVideoCall(testerIdentifier);
				}
				clearTimeout(this.disconnectTimeouts.get(testerIdentifier));
				this.disconnectTimeouts.delete(testerIdentifier);
			}, Constants.ForceExit.DEFAULT_LIMIT_MILLISECONDS));
		}

		this.toggleVideoMonitoring(testerIdentifier, false);
	}

	attendeeSuspendedVideo(testerIdentifier) {
		if (this.isIdentifierProctor(testerIdentifier) ||
			!this.idAndNameMap.has(testerIdentifier)) {
			return;
		}

		if (this.testerConnectionStatuses.get(testerIdentifier) === Constants.TesterVideoConnectionToSession.DISCONNECTED){
			return;
		}
		this.testerConnectionStatuses.set(testerIdentifier, Constants.TesterVideoConnectionToSession.SUSPENDED);
		this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("proctorWidget:cameraNotWorking"), rtp_i18next.t("videoMonitoring:noVideoAvailable"));

		this.suspendTimeouts.set(testerIdentifier, setTimeout(() => {
			if (this.testerConnectionStatuses.get(testerIdentifier) === Constants.TesterVideoConnectionToSession.SUSPENDED) {
				this.forceExitTesterFromVideoCall(testerIdentifier);
			}
			clearTimeout(this.suspendTimeouts.get(testerIdentifier));
			this.suspendTimeouts.delete(testerIdentifier);
		}, Constants.ForceExit.DEFAULT_LIMIT_MILLISECONDS));

		this.toggleVideoMonitoring(testerIdentifier, false);
	}

	forceExitTesterFromVideoCall(testerIdentifier) {
		// If the force exit is for the proctor themselves, do nothing
		if(testerIdentifier === this.clientId){
			return;
		}

		this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("proctorWidget:testerExited"), rtp_i18next.t("videoMonitoring:noVideoAvailable"));
		this.testerConnectionStatuses.set(testerIdentifier, Constants.TesterVideoConnectionToSession.DISCONNECTED);

		// To update the tester's video monitoring status in history
		const message = {
			videoMonitoringStatus: Constants.TesterVideoConnectionToSession.DISCONNECTED
		};
		this.publishMessage(
			testerIdentifier,
			RTPaaS.createMessage(
				Payload.createPayload(
					this.clientId,
					Constants.Role.PROCTOR,
					testerIdentifier,
					Constants.MessageNames.CALL,
					Constants.EventTypes.VIDEO_MONITORING_STATUS_UPDATE,
					message
				)
			)
		);

		this.toggleVideoMonitoring(testerIdentifier, false);
		this.monitoringAudioVideoClient.removeAttendeeByProctor(testerIdentifier);
		this.notifyTDSToExit(testerIdentifier);
		console.log(`RTPaaSProctor: Tester ${testerIdentifier} has been removed from the video monitoring call due to prolonged disconnection or suspension.`);
	}

	// Callback when a tester reconnects to the video monitoring call
	attendeeConnectedAgainCallback(testerIdentifier) {
		if (this.testerConnectionStatuses.get(testerIdentifier) === Constants.TesterVideoConnectionToSession.CONNECTED ||
			this.testerConnectionStatuses.get(testerIdentifier) === Constants.TesterVideoConnectionToSession.SUSPENDED ||
			this.testerConnectionStatuses.get(testerIdentifier) === Constants.TesterVideoConnectionToSession.DISCONNECTED) {
			return;
		}
		clearTimeout(this.disconnectTimeouts.get(testerIdentifier));
		this.disconnectTimeouts.delete(testerIdentifier);
		this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("videoMonitoring:videoReconnecting"), rtp_i18next.t("generalWords:loading"));
		this.testerConnectionStatuses.set(testerIdentifier, Constants.TesterVideoConnectionToSession.CONNECTING);
		setTimeout(() => {
			this.testerConnectionStatuses.set(testerIdentifier, Constants.TesterVideoConnectionToSession.CONNECTED);
			this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("generalWords:callConnected"));
			clearTimeout(this.suspendTimeouts.get(testerIdentifier));
			this.suspendTimeouts.delete(testerIdentifier);
			this.toggleVideoMonitoring(testerIdentifier, true);
		}, 2000);
	}

	// Notify the tester to exit the video call
	notifyTDSToExit(testerIdentifier){
		this.publishMessage(
			testerIdentifier,
			RTPaaS.createMessage(
				Payload.createPayload(
					this.clientId,
					Constants.Role.PROCTOR,
					testerIdentifier,
					Constants.MessageNames.CALL,
					Constants.EventTypes.FORCE_EXIT_CALL
				)
			)
		);
	}

	async startVideoMonitoring(makeNewCall = false, coordinatedMeetingId = null) {
		try {
			const previousMeetingId = coordinatedMeetingId || (makeNewCall === true ? null : await this.retrievePreviousMeetingId(this.idAndNameMap.keys()));

			const defaultConfigs = this.getDefaultMonitoringConfigs();
			const streamingConfigs = this.config.streamingConfigs || {};

			const params = {
				context: this,
				clientId: this.proctorAttendeeId,
				isAttendeeProctor: true,
				meetingId: previousMeetingId,
				isVideoMonitoringCall: true,
				endCallWithErrorCallback: this.endCallDueToError.bind(this),
				videoQuality: streamingConfigs.videoQuality || defaultConfigs.videoQuality,
				videoPrefix: streamingConfigs.videoPrefix || defaultConfigs.videoPrefix,
				attendeeDisconnectedCallback: this.attendeeDisconnectedCallback.bind(this),
				attendeeConnectedAgainCallback: this.attendeeConnectedAgainCallback.bind(this)
			};

			this.monitoringAudioVideoClient = new AudioVideoClient(params);
			const meetingId = await this.monitoringAudioVideoClient.startCall();
			
			if (!meetingId && previousMeetingId) {
				console.warn('Previous meeting ID found but failed to reconnect. Starting a new meeting.');
				this.monitoringAudioVideoClient.cleanup();
				this.monitoringAudioVideoClient = null;
				params.meetingId = null;
				this.monitoringAudioVideoClient = new AudioVideoClient(params);
				const newMeetingId = await this.monitoringAudioVideoClient.startCall();
				if (!coordinatedMeetingId) {
					// Update session coordination channel with new meetingId
					await this.publishMeetingToSession(newMeetingId);
				}
				return {
					meetingId: newMeetingId,
					wasReconnected: false
				};
			} else {
				if (!coordinatedMeetingId) {
					// Update session coordination channel with new meetingId
					await this.publishMeetingToSession(meetingId);
				}
				return {
					meetingId: meetingId,
					wasReconnected: Boolean(previousMeetingId)
				};
			}
		} catch (error) {
			console.error('Error starting video monitoring:', error);
			return {
				wasReconnected: false,
				meetingId: null
			};
		}
	}

	attachListeners() {
		const events = [
			{ name: 'groupAudioCallInitiated', handler: this.handleGroupCallInitiated.bind(this) }
		];

		events.forEach(({ name, handler }) => {
			$(document).off(name, handler).on(name, handler);
		});
	}
	populateStatusImages() {
		this.testerStatusImages.set(Constants.TesterImageStatus.CAMERA_NOT_WORKING, CaptureImage.textToImage(rtp_i18next.t("proctorWidget:cameraNotWorking"), rtp_i18next.t("proctorWidget:noImageAvailable")));
		this.testerStatusImages.set(Constants.TesterImageStatus.EXITED, CaptureImage.textToImage(rtp_i18next.t("proctorWidget:testerExited"), rtp_i18next.t("proctorWidget:noImageAvailable")));
		this.testerStatusImages.set(Constants.TesterImageStatus.LEFT, CaptureImage.textToImage(rtp_i18next.t("proctorWidget:noTesterConnection"), rtp_i18next.t("proctorWidget:noImageAvailable")));
	}

	createDisplayName(idNameMap){
		// loop over the keys and modify the original this.idAndNameMap map with display name
		for (let key of idNameMap.keys()) {
			let displayName = '';
			let nameInfo = this.idAndNameMap.get(key);
			if (nameInfo && nameInfo.firstName && nameInfo.lastName){
				displayName = nameInfo.firstName + ' ' + nameInfo.lastName.substring(0,1);
			} else {
				displayName = key;
				nameInfo.firstName = key;
				nameInfo.lastName = '';
			}
			nameInfo['displayName'] = displayName;
			this.idAndNameMap.set(key,nameInfo);
		}
	}

	setupAblyChannels(testerIdentifierList){
		for (let testerIdentifier of testerIdentifierList) {
			if (testerIdentifier) {
				this.createChannelWithoutHistory(testerIdentifier);
				this.createChannelWithHistory(testerIdentifier);
				this.subscribeToChannels(testerIdentifier);
				if (this.config.imageFrequencyInSeconds) {
					this.imageFrequency(testerIdentifier, this.config.imageFrequencyInSeconds);
				}
				 this.handleHandHistory(testerIdentifier).then(() => {
						console.log(`Handled Hand history for tester: ${testerIdentifier}`);
					}, (err) => {
						console.log(err);
				});

				this.handleLockHistory(testerIdentifier).then(() => {
						console.log(`Handled Lock history for tester: ${testerIdentifier}`);
					}, (err) => {
						console.log(err);
				});

			}

			//Starts a 10-second timeout timer for testers who are already present to detect camera issues
			this.isTesterPresent(this.getChannelWithHistory(testerIdentifier)).then(
				(isTesterPresent) => {
					if (isTesterPresent) {
						this.imageTimeouts.set(testerIdentifier, this.setImageTimeout(testerIdentifier, 10));
					}
				}
			)
		}
	}

	subscribeToChannels(testerIdentifier) {
		this.subscribeToChannelWithoutHistory(testerIdentifier, Constants.MessageNames.IMAGE, this.handleImageMessage.bind(this));
		this.subscribeToChannel(testerIdentifier, Constants.MessageNames.HAND, this.handleHandMessage.bind(this));
		this.subscribeToChannel(testerIdentifier, Constants.MessageNames.LOCK, this.handleLockMessage.bind(this));
		this.subscribeToChannel(testerIdentifier, Constants.MessageNames.CHAT, this.handleChatMessage.bind(this));
		this.subscribeToChannel(testerIdentifier, Constants.MessageNames.CALL, this.handleCallMessage.bind(this));
		this.subscribeToChannel(testerIdentifier, Constants.MessageNames.SYSTEM_CHECK, this.handleSystemCheckStatus.bind(this));
	}

	getNewStudentDataFromProctor(idWithNameJson){
		let newIdAndNameMap = new Map(Object.entries(idWithNameJson));
		this.setupAblyChannels(newIdAndNameMap.keys());
		newIdAndNameMap.forEach((value, key) => this.idAndNameMap.set(key, value));
		this.createDisplayName(newIdAndNameMap);
		newIdAndNameMap.forEach((value, key) => {
			this.testerLockStatuses.set(key, Boolean(this.config?.groupCallFirst));
			this.testerImageStatuses.set(key, Constants.TesterImageStatus.NOT_JOINED);
			this.testerConnectionStatuses.set(key, Constants.TesterVideoConnectionToSession.PENDING);
			this.updateTesterImageByText(key, rtp_i18next.t("videoMonitoring:videoWaiting"));
		});
	}

	postMessage(message) {
		let proctorWindow = this.kendoWindows.get(message.data.testerIdentifier);
		if (proctorWindow) {
			proctorWindow.postMessage(message);
		}
	}

	postNotificationMsg(textMsg, testerIdentifier) {
		if (this.kendoWindows.get(testerIdentifier)) {
			let kendoChat = this.kendoWindows.get(testerIdentifier).kendoChat;

			kendoChat.renderAttachments({
				attachments: [{
					contentType: "notification",
					content: {
						title: "Notification",
						text: textMsg,
						timestamp: new Date()
					}
				}],
				attachmentLayout: "list"
			}, kendoChat.getUser());
		}
	}

	handleImageMessage(message){
		if (this.isMessageSentFromDifferentProctor(message)) {
			return;
		}
		let testerIdentifier = message.data.testerIdentifier;
		switch(message.data.eventType) {
			case Constants.EventTypes.TESTER_IMAGE:
				this.testerImageStatuses.set(testerIdentifier, Constants.TesterImageStatus.ACTIVE);
				//Start a 10-second timer after receiving an image.  If the timer reaches 0, a message will display.
				this.imageTimeouts.set(testerIdentifier, this.setImageTimeout(testerIdentifier, 10));
				if (this.config.testerImageHandler) {
					this.config.testerImageHandler(message);
				}

				if (this.config?.streamingConfigs.monitoringType === Constants.MonitoringType.BY_VIDEO && this.videoMonitoringMeetingId) {
					const messageContents = {
						meetingId: this.videoMonitoringMeetingId,
						videoQuality: this.config.streamingConfigs.videoQuality || this.getDefaultMonitoringConfigs().videoQuality,
						videoMonitoringStatus: Constants.TesterVideoConnectionToSession.PENDING
					};
					this.publishMessage(
						testerIdentifier,
						RTPaaS.createMessage(
							Payload.createPayload(
								this.clientId,
								Constants.Role.PROCTOR,
								testerIdentifier,
								Constants.MessageNames.CALL,
								Constants.EventTypes.VIDEO_MONITORING_CALL,
								messageContents
							)
						)
					);
				}
				break;
			case Constants.EventTypes.TESTER_LEFT:
				if (this.config?.streamingConfigs.monitoringType === Constants.MonitoringType.BY_VIDEO && this.videoMonitoringMeetingId) {
					this.testerImageStatuses.set(testerIdentifier, Constants.TesterImageStatus.EXITED);
					this.setStatusImage(testerIdentifier);
					this.monitoringAudioVideoClient.removeAttendeeByProctor(testerIdentifier);
					clearTimeout(this.disconnectTimeouts.get(testerIdentifier));
					this.testerConnectionStatuses.set(testerIdentifier, Constants.TesterVideoConnectionToSession.DISCONNECTED);
					this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("proctorWidget:testerExited"), rtp_i18next.t("videoMonitoring:noVideoAvailable"));
					this.toggleVideoMonitoring(testerIdentifier, false);

					// To update the tester's video monitoring status in history
					const message = {
						videoMonitoringStatus: Constants.TesterVideoConnectionToSession.DISCONNECTED
					};
					this.publishMessage(
						testerIdentifier,
						RTPaaS.createMessage(
							Payload.createPayload(
								this.clientId,
								Constants.Role.PROCTOR,
								testerIdentifier,
								Constants.MessageNames.CALL,
								Constants.EventTypes.VIDEO_MONITORING_STATUS_UPDATE,
								message
							)
						)
					);
				}
				break;
		}
	}

	/**
	 * Updates the tester's image with the provided text by creating a new image from the text
	 * and passing it to the tester image handler configuration
	 * @param {string} testerIdentifier - Unique identifier for the tester
	 * @param {string} text1 - Text to be converted into an image and displayed
	 * @param {string} text2 - Text to be converted into an image and displayed at line 2
	 */
	updateTesterImageByText(testerIdentifier, text1, text2 = ' ') {
		text2 = text2.length < 1 ? ' ' : text2;
		const newImageData = {
			data: {
				testerIdentifier: testerIdentifier,
				text: CaptureImage.textToImage(text1, text2)
			}
		};
		this.config?.testerImageHandler(newImageData);
	}

	/**
	 * Toggles between video and image elements for a tester's monitoring display.
	 * When enabled, shows the video element and hides the image element.
	 * When disabled, shows the image element and hides the video element.
	 * @param {string} testerIdentifier - The unique identifier for the tester
	 * @param {boolean} enable - Whether to enable (true) or disable (false) video monitoring
	 */
	toggleVideoMonitoring(testerIdentifier, enable) {
		const videoElement = $(`#${this.config.streamingConfigs.videoPrefix + testerIdentifier}`);
		const imageElement = $(`#${this.config.streamingConfigs.imagePrefix + testerIdentifier}`);

		if (videoElement?.length && imageElement?.length) {
			enable ? (imageElement.hide(), videoElement.show()) : (imageElement.show(), videoElement.hide());
		}
	}

	imageFrequency(testerIdentifier, imageFrequencyInSeconds) {
		const messageContents = {'text' : imageFrequencyInSeconds};
		this.publishMessageWithoutHistory(testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.IMAGE, Constants.EventTypes.CHANGE_IMAGE_FREQUENCY, messageContents)));
	}

	setImageTimeout(testerIdentifier, timeoutInSeconds) {
		this.clearImageTimeout(testerIdentifier);
		return setTimeout((testerIdentifier) => {
			this.testerImageStatuses.set(testerIdentifier, Constants.TesterImageStatus.CAMERA_NOT_WORKING);
			this.setStatusImage(testerIdentifier);
		}, timeoutInSeconds * 1000, testerIdentifier);
	}

	clearImageTimeout(testerIdentifier) {
		clearTimeout(this.imageTimeouts.get(testerIdentifier));
	}

	networkIssueDetected() {
		for (let testerIdentifier of this.idAndNameMap.keys()) {
			if (this.testerConnectionStatuses.get(testerIdentifier) !== Constants.TesterVideoConnectionToSession.DISCONNECTED &&
				this.testerImageStatuses.get(testerIdentifier) !== Constants.TesterImageStatus.NOT_JOINED) {
				this.setTextImage(testerIdentifier, rtp_i18next.t("proctorWidget:networkIssues"), rtp_i18next.t("proctorWidget:noImageAvailable"));
			}
		}

		// Notify the monitoring audio video client of the network issue
		if (this.monitoringAudioVideoClient) {
			//TODO: Add any specific handling needed for network issues in video monitoring
		}

		// Handling for network issues if in group audio call
		if (this.broadcastObj?.broadcastWidgetWindow.broadcastKendoWindow.options.visible){
			this.broadcastObj?.endGroupAudioCallDueToNetworkIssue(Constants.EndGroupCallReason.NETWORK_ISSUE).then(() => {
				this.setIsGroupAudioCallRunning(false);
				this.cleanActiveCallList();
				this.broadcastObj = null;
				console.warn("End group audio call due to network issue");
			});
		}
	}

	handleLockMessage(message) {
		let testerIdentifier = message.data.testerIdentifier;
		//There could be more event types in the future, so use a switch statement.
		switch (message.data.eventType) {
			case Constants.EventTypes.UNLOCK_TESTER_ON_PROCTOR_SIDE:
				this.unlockTesterOnProctorSide(testerIdentifier);
				break;
		}
	}

	handleHandMessage(message) {
		let testerIdentifier = message.data.testerIdentifier;

		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		//We still want to process the message even if the proctor hasn't created a window yet, since the implementor might have their own callback methods.
		switch (message.data.eventType) {
			case Constants.EventTypes.LOWER_HAND:
				this.lowerTheHand(proctorWindow, testerIdentifier, message);
				break;
			case Constants.EventTypes.RAISE_HAND:
				if (this.config.raiseHandHandler) {
					this.config.raiseHandHandler(message);
				}
				this.raiseTheHand(proctorWindow, testerIdentifier);
				break;
		}
	}

	raiseTheHand(proctorWindow, testerIdentifier) {
		if (proctorWindow) {
			proctorWindow.setHandIconRaised();
			if (this.dockManager.overflowWindows.has(testerIdentifier)) {
				$(`.overflow-element[data-identifier='${testerIdentifier}']`).trigger('raise-hand');
				this.dockManager.updateDockBaseOverflowElement();
			}
		}
		this.testerHandStatuses.set(testerIdentifier, true);
		if (this.getIsInGroupAudioCall()) {
			this.broadcastObj.broadcastWidgetWindow.setHandIconRaised(testerIdentifier);
		}
	}

	lowerTheHand(proctorWindow, testerIdentifier, message) {
		if (this.config.lowerHandHandler) {
			if (message) {
				this.config.lowerHandHandler(message);
			} else {
				this.config.lowerHandHandler(RTPaaS.createMessage(Payload.createPayload(null, Constants.Role.TESTER, testerIdentifier, Constants.MessageNames.HAND, Constants.EventTypes.LOWER_HAND)));
			}
		}
		if (proctorWindow) {
			proctorWindow.setHandIconLowered();
			if (this.dockManager.overflowWindows.has(testerIdentifier)) {
				$(`.overflow-element[data-identifier='${testerIdentifier}']`).trigger('lower-hand');
				this.dockManager.updateDockBaseOverflowElement();
			}
		}
		this.testerHandStatuses.set(testerIdentifier, false);
		this.broadcastObj?.broadcastWidgetWindow?.setHandIconLowered(testerIdentifier);
	}

	lowerHand(testerIdentifier) {
		const message = RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.HAND, Constants.EventTypes.LOWER_HAND));
		this.publishMessage(testerIdentifier, message);
		this.lowerTheHand(this.kendoWindows.get(testerIdentifier), testerIdentifier, message);
	}


	sendMuteMessage(testerIdentifier, muted) {
		const messageContents = {'muted' : muted};
		let message = RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.BROADCAST, Constants.EventTypes.BROADCAST_TOGGLE_MUTE, messageContents));
		this.publishMessage(testerIdentifier, message);
	}

	handleChatMessage(message) {
		switch(message.data.eventType) {
			case Constants.EventTypes.INITIATE_CHAT:
				if (this.isMessageSentFromDifferentProctor(message)) {
					let proctorWindow = this.kendoWindows.get(message.data.testerIdentifier);
					if (proctorWindow && !proctorWindow.isChatEnabled) {
						proctorWindow.windowDiv.trigger('enableEndChatBtn');
						this.postNotificationMsg(rtp_i18next.t("proctorWidget:chatStartedByOtherProctor"), message.data.testerIdentifier);
					}
					break;
				}
				break;
			case Constants.EventTypes.RECEIVED_CHAT:
				let proctorWindow = this.kendoWindows.get(message.data.testerIdentifier);
				if (proctorWindow) {
					let messages = proctorWindow.chatDiv.find(".rtpChatMessage");
					if (messages && messages.length > 0) {
						for (let msgIdx = 0; msgIdx < messages.length; msgIdx++) {
							let messageDiv = messages[msgIdx];
							if (messageDiv && messageDiv.id === message.data.msgId) {
								let fontIcons = messageDiv.getElementsByClassName('k-font-icon');
								for (let i = 0; i < fontIcons.length; i++) {
									let fontRandomId = 'font-'+crypto.randomUUID();
									fontIcons[i].id = fontRandomId;
									let tooltipElement = $('#'+fontRandomId);
									if (message.data.msgSeen) {
										proctorWindow.changeChatStatusIcon(fontIcons[i], tooltipElement, CustomKendoWindow.CHAT_MSG_SEEN);
									} else {
										proctorWindow.changeChatStatusIcon(fontIcons[i], tooltipElement, CustomKendoWindow.CHAT_MSG_RECEIVED);
									}
								}
							}
						}
					}
				}

				break;
			case Constants.EventTypes.SEND_CHAT:
				let proctorWindowForChat = this.kendoWindows.get(message.data.testerIdentifier);
				if (this.isMessageSentFromDifferentProctor(message)) {
					if (proctorWindowForChat && !proctorWindowForChat.isChatEnabled) {
						proctorWindowForChat.windowDiv.trigger('enableEndChatBtn');
						this.postNotificationMsg(rtp_i18next.t("proctorWidget:chatStartedByOtherProctor"), message.data.testerIdentifier);
					}
				}
				this.postMessage(message);
				this.sendReceivedChatMessage(proctorWindowForChat, message.data.testerIdentifier, message.data.msgId);
				break;
			case Constants.EventTypes.END_CHAT:
				if (this.isMessageSentFromDifferentProctor(message)) {
					let proctorWindow = this.kendoWindows.get(message.data.testerIdentifier);
					if (proctorWindow) {
						proctorWindow.windowDiv.trigger('disableEndChatBtn');
						this.postNotificationMsg(rtp_i18next.t("proctorWidget:chatEndedByOtherProctor"), message.data.testerIdentifier);
					}
				}
				break;
		}
	}

	initiateKendoChat(testerIdentifier) {
		if (!this.kendoWindows) {
			return;
		}
		// First, check the map to see if we already have a window
		if (this.kendoWindows.has(testerIdentifier)) {
			console.log("initiateKendoChat() open window");
			return;
		}

		let displayName = this.getTesterDisplayName(testerIdentifier);
		// Create the kendo window, which includes chat
		let proctorWindow = new ProctorKendoWindow(this, testerIdentifier, displayName);
		if (this.isInVideoCall) {
			proctorWindow.disableVideoBtn();
		}
		this.kendoWindows.set(testerIdentifier, proctorWindow);
		this.handleHistory(proctorWindow, testerIdentifier).then(
			() => {
				this.handleTesterPresence(proctorWindow, testerIdentifier);
				this.setCallAndGroupCallAvailability(testerIdentifier, true);
			},
			(err) => {
				console.log(err);
			}
		);
	}

	getTesterDisplayName(testerIdentifier){
		return this.idAndNameMap.get(testerIdentifier).displayName;
	}

	getTesterFirstName(testerIdentifier){
		return this.idAndNameMap.get(testerIdentifier).firstName;
	}

	getTesterLastName(testerIdentifier){
		return this.idAndNameMap.get(testerIdentifier).lastName;
	}

	initiateChat(testerIdentifier) {
		//if the chat has already been initiated, check if it's already open
		if (this.kendoWindows && this.kendoWindows.has(testerIdentifier)) {
			let proctorWindow = this.kendoWindows.get(testerIdentifier);
			let kendoWindow = proctorWindow.kendoWindow;
			if (this.dockManager.overflowWindows.has(testerIdentifier)) {
				this.dockManager.removeFromOverflow(testerIdentifier);
				kendoWindow.restore();
				this.dockManager.centerWithOffset(proctorWindow.kendoWindow);
				this.dockManager.updateDockBaseOverflowElement();
			} else {
				//if the window was closed, re-open it in the center and re-initiate it on the tester side
				if (!kendoWindow.options.visible) {
					kendoWindow.open();
					this.enableChat(testerIdentifier);
				} else {
					kendoWindow.restore();
				}
			}
			return;
		}
		this.initChat(testerIdentifier);
	}

	initChat(testerIdentifier){
		this.publishMessage(testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.CHAT, Constants.EventTypes.INITIATE_CHAT)));
		this.initiateKendoChat(testerIdentifier);
	}

	chat(testerIdentifier, text, msgId) {
		this.enableChatIfNecessary(testerIdentifier);
		const messageContents = {text : text, msgId: msgId};
		this.publishMessage(testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR,
			testerIdentifier, Constants.MessageNames.CHAT, Constants.EventTypes.SEND_CHAT, messageContents)));
		let that = this;
		setTimeout(function () {
			that.checkIfMessageWasReceived(testerIdentifier, msgId);
		}, 2 * 1000);
	}

	checkIfMessageWasReceived(testerIdentifier, msgId) {
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		let messages = proctorWindow.chatDiv.find(".rtpChatMessage");
		if (messages && messages.length > 0) {
			for (let msgIdx = 0; msgIdx < messages.length; msgIdx++) {
				let messageDiv = messages[msgIdx];
				if (messageDiv && messageDiv.id === msgId) {
					let fontIcons = messageDiv.getElementsByClassName('k-font-icon');
					for (let i = 0; i < fontIcons.length; i++) {
						if (!fontIcons[i].classList.contains('k-i-check-outline') && !fontIcons[i].classList.contains('k-i-check-circle')) {
							let fontRandomId = crypto.randomUUID();
							fontIcons[i].id = 'font-' + fontRandomId;
							let tooltipElement = $('#font-' + fontRandomId);
							proctorWindow.changeChatStatusIcon(fontIcons[i], tooltipElement, CustomKendoWindow.CHAT_MSG_NOTSEEN);
						}
					}
				}
			}
		}
	}

	endChat(testerIdentifier) {
		this.publishMessage(testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.CHAT, Constants.EventTypes.END_CHAT)));
	}

	closeChatWindow(testerIdentifier) {
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		this.disableChat(testerIdentifier);
		if (proctorWindow && proctorWindow.isVideoCallRunning()) {
			this.endCall(testerIdentifier);
			proctorWindow.switchToChat();
			this.determineAllStartButtonStatuses(testerIdentifier);
		}
	}

	enableChatIfNecessary(testerIdentifier){
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		if (proctorWindow && !proctorWindow.isChatEnabled) {
			this.enableChat(testerIdentifier);
		}
	}

	enableChat(testerIdentifier) {
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		if (proctorWindow) {
			proctorWindow.windowDiv.trigger('enableEndChatBtn');
			this.initChat(testerIdentifier);
		}
	}

	disableChat(testerIdentifier) {
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		if (proctorWindow) {
			proctorWindow.windowDiv.trigger('disableEndChatBtn');
			this.endChat(testerIdentifier);
		}
	}

	handleCallMessage(message) {
		let testerIdentifier = message.data.testerIdentifier;
		let kendoWindow = this.kendoWindows.get(testerIdentifier);
		switch(message.data.eventType) {
			case Constants.EventTypes.INITIATE_CALL:
				if (this.isMessageSentFromDifferentProctor(message)) {
					this.addTesterToActiveCallList(testerIdentifier);
					kendoWindow?.disableVideoBtn();
					this.postNotificationMsg(`${this.idAndNameMap.get(testerIdentifier).firstName} ${rtp_i18next.t("proctorWidget:testerInCall")}`, testerIdentifier);
				}
				break;
			case Constants.EventTypes.END_CALL:
				if (this.isMessageSentFromDifferentProctor(message)) {
					this.removeTesterFromActiveCallList(testerIdentifier);
					this.postNotificationMsg(`${this.idAndNameMap.get(testerIdentifier).firstName} ${rtp_i18next.t("proctorWidget:testerNotInCall")}`, testerIdentifier);
					break;
				}
				if (Constants.Role.TESTER === message.data.senderType) {
					this.setVideoStatusText(testerIdentifier, rtp_i18next.t("proctorWidget:testerDisconnected"));
				}
				this.endCallAndSwitchToChat(testerIdentifier);
				break;
			case Constants.EventTypes.ANSWER_CALL:
				if (!kendoWindow) {
					break;
				}
				if (this.config.answerCallHandler) {
					this.config.answerCallHandler(message);
				}
				this.setVideoStatusText(testerIdentifier, rtp_i18next.t("generalWords:callConnected"));
				this.cancelFailedToConnectToTesterTimeout();
				this.stopCallChime();
				break;
			case Constants.EventTypes.UNANSWERED_CALL:
				if (kendoWindow?.isVideoCallRunning()) {
					this.endCallAndSwitchToChat(testerIdentifier);
					this.postNotificationMsg(rtp_i18next.t("proctorWidget:testerMissedCall"), testerIdentifier);
					this.cancelFailedToConnectToTesterTimeout();
					this.stopCallChime();
				} else {
					this.postNotificationMsg(`${this.idAndNameMap.get(testerIdentifier).firstName} ${rtp_i18next.t("proctorWidget:testerNotInCall")}`, testerIdentifier);
				}
				this.removeTesterFromActiveCallList(testerIdentifier);
				break;
			case Constants.EventTypes.TESTER_LEFT_CALL:
				if (kendoWindow?.isVideoCallRunning()) {
					this.endCallAndSwitchToChat(testerIdentifier);
					this.postNotificationMsg(rtp_i18next.t("proctorWidget:testerEndedCall"), testerIdentifier);
				} else {
					this.postNotificationMsg(`${this.idAndNameMap.get(testerIdentifier).firstName} ${rtp_i18next.t("proctorWidget:testerNotInCall")}`, testerIdentifier);
				}
				this.removeTesterFromActiveCallList(testerIdentifier);
				this.stopCallChime();
				break;
			case Constants.EventTypes.TESTER_FAILED_TO_CONNECT_CALL:
				this.failedToConnectToTester(testerIdentifier);
				break;
			case Constants.EventTypes.TESTER_ALREADY_IN_CALL:
				//Two proctors tried calling the same tester.  This could be a combination of group calls or video calls.  End whichever call is relevant.
				if (kendoWindow?.isVideoCallRunning()) {
					this.endCallAndSwitchToChat(testerIdentifier);
					this.postNotificationMsg(`${this.idAndNameMap.get(testerIdentifier).firstName} ${rtp_i18next.t("proctorWidget:testerInCall")}`, testerIdentifier);
				}
				if (this.getIsInGroupAudioCall()) {
					this.broadcastObj.broadcastWidgetWindow.postNotificationMsg(rtp_i18next.t("proctorWidget:callInProgress"));
					this.setIsInGroupAudioCall(false);
					this.broadcastObj.cancelGroupCall();
				}
				this.removeTesterFromActiveCallList(testerIdentifier);
				break;
			case Constants.EventTypes.TESTER_NOT_IN_CALL:
				this.removeTesterFromActiveCallList(testerIdentifier);
				break;
			case Constants.EventTypes.PROCTOR_FAILED_TO_CONNECT_CALL:
				this.removeTesterFromActiveCallList(testerIdentifier);
				break;
			case Constants.EventTypes.ANSWER_VIDEO_MONITORING_CALL:
				// Update the tester's image to show "Loading..." while connecting to the video monitoring call.
				this.updateTesterImageByText(
					message.data.clientId,
					rtp_i18next.t("generalWords:loading")
				);
				this.testerConnectionStatuses.set(
					message.data.clientId,
					Constants.TesterVideoConnectionToSession.CONNECTING
				);

				//To give a sense of progress, change the text to "Connected" for a second, then switch to video monitoring.
				setTimeout(() => {
					this.testerConnectionStatuses.set(
						message.data.clientId,
						Constants.TesterVideoConnectionToSession.CONNECTED
					);
					this.updateTesterImageByText(
						message.data.clientId,
						rtp_i18next.t("generalWords:callConnected")
					);

					// To update the tester's video monitoring status in history
					const messageContents = {
						videoMonitoringStatus: Constants.TesterVideoConnectionToSession.CONNECTED
					};
					this.publishMessage(
						testerIdentifier,
						RTPaaS.createMessage(
							Payload.createPayload(
								this.clientId,
								Constants.Role.PROCTOR,
								testerIdentifier,
								Constants.MessageNames.CALL,
								Constants.EventTypes.VIDEO_MONITORING_STATUS_UPDATE,
								messageContents
							)
						)
					);

					setTimeout(() => {
						this.toggleVideoMonitoring(testerIdentifier, true);
					}, Constants.VideoCapture.CONNECTING_DELAY_MS);
				}, Constants.VideoCapture.CONNECTING_DELAY_MS);
				break;
			case Constants.EventTypes.LEFT_VIDEO_MONITORING_CALL:
				if (this.config?.streamingConfigs.monitoringType === Constants.MonitoringType.BY_VIDEO && this.videoMonitoringMeetingId) {
					this.forceExitTesterFromVideoCall(testerIdentifier);
				}
				break;
			case Constants.EventTypes.TESTER_SUSPEND_VIDEO_MONITORING:
				if (this.config?.streamingConfigs.monitoringType === Constants.MonitoringType.BY_VIDEO && this.videoMonitoringMeetingId) {
					this.attendeeSuspendedVideo(testerIdentifier);
				}
				break;
			case Constants.EventTypes.TESTER_RESTARTING_VIDEO_MONITORING:
				if (this.config?.streamingConfigs.monitoringType === Constants.MonitoringType.BY_VIDEO && this.videoMonitoringMeetingId) {
					//prevent multiple simultaneous restarts, requested by the different testers at the same time
					if (!this.isRestartingVideoMonitoringInProgress) {
						this.isRestartingVideoMonitoringInProgress = true;
						this.restartVideoMonitoringByProctor().then(
							() => {
								console.log(`Successfully restarted video monitoring based on a request from ${testerIdentifier}.`);
								this.isRestartingVideoMonitoringInProgress = false;
							},
							(err) => {
								console.log(err);
								this.isRestartingVideoMonitoringInProgress = false;
							}
						);
					}
				}
				break;
			case Constants.EventTypes.TESTER_FAILED_TO_CONNECT_VIDEO_MONITORING_CALL:
				if (this.config?.streamingConfigs.monitoringType === Constants.MonitoringType.BY_VIDEO && this.videoMonitoringMeetingId) {
					this.testerConnectionStatuses.set(testerIdentifier, Constants.TesterVideoConnectionToSession.DISCONNECTED);
					this.updateTesterImageByText(testerIdentifier, rtp_i18next.t("videoMonitoring:testerCouldNotJoin"), rtp_i18next.t("videoMonitoring:noVideoAvailable"));
					this.toggleVideoMonitoring(testerIdentifier, false);
				}
				break;
		}
	}

	unlockAllTesters(attendeesInCall) {
		this.idAndNameMap.forEach((_, testerIdentifier) => {
			//Only unlock testers that are currently in a broadcast call.
			if (attendeesInCall?.has(testerIdentifier)) {
				this.unlockTester(testerIdentifier);
			}
		});
	}

	sendUnlockMessage(testerIdentifier) {
		//Send an unlocked message to other proctors so that if the tester is locked due to a different proctor, they will be unlocked.
		let messageToProctor = RTPaaS.createMessage(
			Payload.createPayload(
				this.clientId,
				Constants.Role.PROCTOR,
				testerIdentifier,
				Constants.MessageNames.LOCK,
				Constants.EventTypes.UNLOCK_TESTER_ON_PROCTOR_SIDE
			)
		);
		this.publishMessage(testerIdentifier, messageToProctor);

		//Send an unlocked message to the tester so that if the tester is locked due to a different proctor, they will be unlocked.
		let messageToTesters = RTPaaS.createMessage(
			Payload.createPayload(
				this.clientId,
				Constants.Role.PROCTOR,
				testerIdentifier,
				Constants.MessageNames.LOCK,
				Constants.EventTypes.UNLOCK_TESTER
			)
		);
		this.publishMessage(testerIdentifier, messageToTesters);
	}

	// This method is called when the proctor clicks the unlock button on their side,
	// or when an unlock message is received from another proctor.
	// It will update the lock status and UI
	unlockTesterOnProctorSide(testerIdentifier) {
		this.testerLockStatuses.set(testerIdentifier, false);
		let proctorWindow = this.kendoWindows?.get(testerIdentifier);
		proctorWindow?.hideUnlockButton();
		//UI - TMS side to handle the unlocking
		if (this.config.unlockByIdHandler){
			this.config.unlockByIdHandler(testerIdentifier);
		} else {
			console.warn("No unlockByIdHandler defined in the config.");
		}
	}

	//A public method to unlock a specific tester.
	unlockTester(testerIdentifier) {
		this.sendUnlockMessage(testerIdentifier);
		this.testerLockStatuses.set(testerIdentifier, false);
		let proctorWindow = this.kendoWindows?.get(testerIdentifier);
		proctorWindow?.hideUnlockButton();
		//UI - TMS side to handle the unlocking
		if (this.config.unlockByIdHandler){
			this.config.unlockByIdHandler(testerIdentifier);
		} else {
			console.warn("No unlockByIdHandler defined in the config.");
		}
	}

	async initiateCall(testerIdentifier) {
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		if (proctorWindow.isVideoCallRunning()) {
			return;
		}
		CustomKendoWindow.hideMinimizeButton(proctorWindow.kendoWindow);
		this.toggleGroupCallButton(false);
		await proctorWindow.videoWindow.startCall();
		proctorWindow.setVideoCallRunning(true);
		proctorWindow.changeVideoLabelToInCall(testerIdentifier);
		this.isInVideoCall = true;
		this.setVideoStatusText(testerIdentifier, rtp_i18next.t("generalWords:callConnecting"));
		return proctorWindow.videoWindow.audioVideoClient.meetingId;
	}

	isSwitchToVideoAllowed(testerIdentifier) {
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		if (!this.isAblyConnected && (!proctorWindow || !proctorWindow.isVideoCallRunning())) {
			this.postNotificationMsg(rtp_i18next.t("proctorWidget:networkDown"), testerIdentifier);
			return false;
		} else {
			return true;
		}
	}

	switchToVideo(testerIdentifier) {
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		if (proctorWindow.isVideoCallRunning()) {
			return;
		}
		this.disableAllOtherStartButtons(testerIdentifier);

		this.initiateCall(testerIdentifier).then((meetingId) => {
			const messageContents = {text : meetingId};
			this.activeCallTesterIdentifier = testerIdentifier;
			this.publishMessage(testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.CALL, Constants.EventTypes.INITIATE_CALL, messageContents)));
		});
		this.startCallChime();
		this.createFailedToConnectToTesterTimeout(testerIdentifier);
	}

	createFailedToConnectToTesterTimeout(testerIdentifier) {
		if (!this.failedToConnectToTesterTimeoutId) {
			this.failedToConnectToTesterTimeoutId = setTimeout(() => {
				this.failedToConnectToTesterDueToTimeout(testerIdentifier);
			}, 35 * 1000);
		}
	}

	failedToConnectToTester(testerIdentifier) {
		this.removeTesterFromActiveCallList(testerIdentifier);
		let kendoWindow = this.kendoWindows.get(testerIdentifier);
		if (!kendoWindow) {
			return;
		}
		if (kendoWindow.isVideoCallRunning()) {
			this.endCallAndSwitchToChat(testerIdentifier);
			this.postNotificationMsg(rtp_i18next.t("proctorWidget:testerCallFailed"), testerIdentifier);
			this.setVideoStatusText(testerIdentifier, rtp_i18next.t("proctorWidget:testerUnableToConnect"));
		}
		this.cancelFailedToConnectToTesterTimeout();
		this.stopCallChime();
	}

	failedToConnectToTesterDueToTimeout(testerIdentifier) {
		this.failedToConnectToTester(testerIdentifier);
		const messageContents = {text: "failedToConnectToTesterDueToTimeout() testerIdentifier="+testerIdentifier};
		this.publishMessage(testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.CALL, Constants.EventTypes.PROCTOR_FAILED_TO_CONNECT_CALL, messageContents)));
		this.removeTesterFromActiveCallList(testerIdentifier);
		this.stopCallChime();
	}

	cancelFailedToConnectToTesterTimeout() {
		if (this.failedToConnectToTesterTimeoutId) {
			clearTimeout(this.failedToConnectToTesterTimeoutId);
			this.failedToConnectToTesterTimeoutId = null;
		}
	}

	endCall(testerIdentifier) {
		this.publishMessage(testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.CALL, Constants.EventTypes.END_CALL)));
		this.postNotificationMsg(rtp_i18next.t("proctorWidget:proctorLeftCall"), testerIdentifier);
		this.handleEndCall(testerIdentifier);
	}

	endCallDueToError(testerIdentifier, extraDisplayText, doNotNotifyTester) {
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		if (proctorWindow && proctorWindow.isVideoCallRunning()) {
			if (!doNotNotifyTester) {
				let messageContents = {text: "endCallDueToError() testerIdentifier=" + testerIdentifier + " extraDisplayText=" + extraDisplayText};
				this.publishMessage(testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.CALL, Constants.EventTypes.PROCTOR_FAILED_TO_CONNECT_CALL, messageContents)));
			}
			if (extraDisplayText) {
				this.postNotificationMsg(rtp_i18next.t("proctorWidget:proctorCallFailed") + " " + extraDisplayText, testerIdentifier);
			} else {
				this.postNotificationMsg(rtp_i18next.t("proctorWidget:proctorCallFailed"), testerIdentifier);
			}
		}
		this.handleEndCall(testerIdentifier);
	}

	endCallAndSwitchToChat(testerIdentifier) {
		if (this.config.endCallHandler) {
			this.config.endCallHandler(message);
		}
		this.handleEndCall(testerIdentifier);
	}

	handleEndCall(testerIdentifier){
		this.activeCallTesterIdentifier = null;
		this.cancelFailedToConnectToTesterTimeout();
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		proctorWindow.videoWindow.endCall();
		CustomKendoWindow.restoreMinimizeButton(proctorWindow.kendoWindow);
		proctorWindow.changeVideoLabelToVideo(testerIdentifier);
		proctorWindow.setVideoCallRunning(false);
		proctorWindow.switchToChat();
		this.removeTesterFromActiveCallList(testerIdentifier);
		this.isInVideoCall = false;
		proctorWindow.enableVideoBtn();
		this.determineAllStartButtonStatuses(testerIdentifier);
		this.stopCallChime();
	}

	async restartVideoMonitoringByProctor() {
		try {
	    // End the current monitoring session if present
	    if (this.videoMonitoringMeetingId && this.monitoringAudioVideoClient) {
	      await this.monitoringAudioVideoClient.endVideoMonitoringCall();
	      this.videoMonitoringMeetingId = null;
	    }

	    // Start a brand-new monitoring session
	    const defaultConfigs = this.getDefaultMonitoringConfigs();
	    const streamingConfigs = this.config.streamingConfigs || {};
	    const { meetingId } = await this.startVideoMonitoring(true);// true indicates a restart

	    if (!meetingId) {
	      console.error('RTPaaSProctor: Failed to restart video monitoring; no meetingId returned.');
		  this.isRestartingVideoMonitoringInProgress = false;
	      return;
	    }

	    this.videoMonitoringMeetingId = meetingId;

		// Update session coordination channel with new meetingId
		await this.publishMeetingToSession(meetingId);

	    // Notify all testers with the new meeting ID and reset status to PENDING
	    for (const [testerIdentifier] of this.idAndNameMap) {
			const messageContents = {
				meetingId: this.videoMonitoringMeetingId,
				videoQuality: streamingConfigs.videoQuality || defaultConfigs.videoQuality,
			};

			// Update local status and UI
			this.updateTesterImageByText(testerIdentifier, rtp_i18next.t('videoMonitoring:videoRestarting'));
			this.toggleVideoMonitoring(testerIdentifier, false);

			// The order of the following two operations is important.
			// Send Ably messages to instruct tester to join the new session
	      	this.publishMessage(
				  testerIdentifier,
				RTPaaS.createMessage(
					Payload.createPayload(
						this.clientId,
						Constants.Role.PROCTOR,
						testerIdentifier,
						Constants.MessageNames.CALL,
						Constants.EventTypes.VIDEO_MONITORING_CALL,
						messageContents
					)
				)
			);
			await this.updateTesterStatusInRefresh(testerIdentifier);
		}
		console.log(`RTPaaSProctor: Restarted video monitoring; new meetingId = ${this.videoMonitoringMeetingId}`);
		} catch (err) {
			console.error('RTPaaSProctor: Error restarting video monitoring:', err);
			this.isRestartingVideoMonitoringInProgress = false;
		}
	}

	setCallAndGroupCallAvailability(testerIdentifier) {
		this.setCallAvailability(testerIdentifier);
		this.setGroupCallAvailability();
	}

	setCallAvailability(testerIdentifier) {
		if (this.isInVideoCall) {
			return;
		}
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		let channel = this.getChannelWithHistory(testerIdentifier);
		this.isTesterPresent(channel).then(
			(isTesterPresent) => {
				if (proctorWindow) {
					proctorWindow.videoBtn.prop("disabled", !isTesterPresent || this.isTesterInCall(testerIdentifier) || this.getIsGroupAudioCallRunning());
				}
			}
		);
	}

	setGroupCallAvailability() {
		let isAnotherProctorInGroupCall = (this.getIsGroupAudioCallRunning() && !this.getIsInGroupAudioCall());
		if (!this.broadcastObj || isAnotherProctorInGroupCall || this.isInVideoCall) {
			this.toggleGroupCallButton(false);
			return;
		}
		let promises = [];
		for (let testerIdentifier of this.idAndNameMap.keys()) {
			promises.push(new Promise((resolve, reject) => {
				let channel = this.getChannelWithHistory(testerIdentifier);
				this.isTesterPresent(channel).then(
					(isTesterPresent) => {
						if (this.isTesterInCall(testerIdentifier)) {
							reject();
						}
						resolve(isTesterPresent);
					}
				);
			}));
		}
		Promise.all(promises).then((values) => {
			//Check the values of all the resolved promises.  If any of them resolved to "true", we know a tester is present, and we can enable the button.
			let isAnyTesterPresent = values.some((item) => {
				return item;
			});
			this.toggleGroupCallButton(isAnyTesterPresent);
		}).catch(() => {
			//Catching here means we rejected one of the promises due to a tester being in a call.  We can safely disable the group call button.
			this.toggleGroupCallButton(false);
		});
	}

	disableAllOtherStartButtons(testIdentifierToKeep) {
		for (let testerIdentifier of this.kendoWindows.keys()) {
			if (testerIdentifier !== testIdentifierToKeep) {
				this.setVideoStatusText(testerIdentifier, "Call in progress: "+testIdentifierToKeep);
				let proctorWindow = this.kendoWindows.get(testerIdentifier);
				if (proctorWindow) {
					proctorWindow.changeVideoLabelToVideo(testerIdentifier);
					proctorWindow.disableVideoBtn();
				}
			}
		}
	}

	determineAllStartButtonStatuses(testIdentifierToKeep) {
		for (let testerIdentifier of this.kendoWindows.keys()) {
			if (testerIdentifier !== testIdentifierToKeep) {
				this.setVideoStatusText(testerIdentifier, '');
				this.setCallAvailability(testerIdentifier);
			}
		}
		this.setGroupCallAvailability();
	}

	setVideoStatusText(testerIdentifier, textToDisplay) {
		const displayVideoStatusText = document.querySelector('#displayVideoStatusText_'+testerIdentifier);
		if (displayVideoStatusText) {
			displayVideoStatusText.innerHTML = textToDisplay;
		}
	}

	enableVideoToolBarBtns(testerIdentifier) {
		let proctorWindow = this.kendoWindows.get(testerIdentifier);
		proctorWindow.videoWindow.setAudioIconStatus(true, true);
		proctorWindow.videoWindow.setVideoIconStatus(true, true);
	}

	handleSystemCheckStatus(message){
		switch(message.data.eventType) {
			case Constants.EventTypes.SYSTEM_CHECK_SUCCESS:
				$(window).trigger('rtpSystemCheckStatus', {
					'testerIdentifier': message.data.testerIdentifier,
					'isSuccess': message.data.isSuccess,
				});
				break;

			case Constants.EventTypes.SYSTEM_CHECK_FAIL:
				$(window).trigger('rtpSystemCheckStatus', {
					'testerIdentifier': message.data.testerIdentifier,
					'isSuccess': message.data.isSuccess,
					'statusCode':message.data.statusCode,
					'statusMsg':message.data.statusMsg
				});
				break;
		}
	}

	determineGroupCallStatus(history) {
		let messages = [Constants.EventTypes.BROADCAST_CALL, Constants.EventTypes.BROADCAST_END_CALL];
		let broadcastCallHistory = history.filter(message => messages.includes(message.data.eventType));
		//if there is no broadcast history, or the last broadcast message is "end call", then there's nothing we need to do.
		if (!Array.isArray(broadcastCallHistory) || !broadcastCallHistory.length || broadcastCallHistory[0].data.eventType === Constants.EventTypes.BROADCAST_END_CALL) {
			return;
		}
		//If the last broadcast message was a call, we need to make sure that there isn't a call that didn't end properly.
		let lastMessage = broadcastCallHistory[0];
		if (lastMessage.data.eventType === Constants.EventTypes.BROADCAST_CALL) {
			if (lastMessage.data.clientId === this.clientId) {
				//If we were the proctor that started the call, it must mean that we left without ending it.  We can end it now.
				this.setIsGroupAudioCallRunning(false);
				this.notifyEndGroupCallToAllStudents();
				return;
			}
			//Here it means that another proctor started the broadcast call.  Let's make sure they're still present.
			let channel = this.getChannelWithHistory(this.broadcastChannelIdentifier);
			this.isSpecificProctorPresent(channel, lastMessage.data.clientId).then((isSpecificProctorPresent) => {
					if (!isSpecificProctorPresent) {
						//The proctor who started the call has left, so we can end it.
						this.setIsGroupAudioCallRunning(false);
						this.notifyEndGroupCallToAllStudents();
					}
				},
				(err) => {
					console.error(`determineGroupCallStatus() Error retrieving proctor presence data: ${err}`);
				});
		}
	}

	// Helper: returns true if the identifier belongs to any proctor
	isIdentifierProctor(identifier) {
		if (!identifier) return false;
		// This proctor
		if (identifier === this.clientId && this.isProctor()) return true;
		return false;
	}

	async initPresenceHandlers(channel) {
		let that = this;

		await channel.presence.subscribe('enter', function(member) {
			let testerIdentifier = member.data.testerIdentifier;
			if (member.data.isProctor) {
				if (member.clientId === that.clientId) {
					testerIdentifier && that.setCallAndGroupCallAvailability(testerIdentifier);
				} else {
					console.log(`RTPaaSProctor: Proctor ${member.clientId} joined.`);
				}
				return;
			}
			//tester is joining
			that.setCallAndGroupCallAvailability(testerIdentifier);
			that.postNotificationMsg(`${that.getTesterFirstName(testerIdentifier)} ${that.getTesterLastName(testerIdentifier)} ${rtp_i18next.t("proctorWidget:testerIsAvailable")}`, testerIdentifier);
			that.imageTimeouts.set(testerIdentifier, that.setImageTimeout(testerIdentifier, 10));
		});

		await channel.presence.subscribe('leave', function (member) {
			let testerIdentifier = member.data.testerIdentifier;
			if (member.clientId === that.clientId) {
				return;
			}

			if (member.data.isProctor) {
				//Another proctor is leaving.  Check if they have an active group call running
				if (member.clientId === that.activeGroupCallProctorId) {
					that.notifyEndGroupCallToAllStudents();
				}
				return;
			}
			//Tester is leaving
			let proctorWindow = that.kendoWindows.get(testerIdentifier);

			//If the tester is leaving before the proctor has initialized the window, proctorWindow won't exist.
			if (proctorWindow) {
				that.postNotificationMsg(`${that.getTesterFirstName(testerIdentifier)} ${that.getTesterLastName(testerIdentifier)} ${rtp_i18next.t("proctorWidget:testerIsNotAvailable")}`, testerIdentifier);
				proctorWindow.videoBtn.prop("disabled", true);
				//End the video call if there is one
				if (proctorWindow.isVideoCallRunning()) {
					that.stopCallChime();
					that.endCallAndSwitchToChat(testerIdentifier);
					that.setCallAndGroupCallAvailability(testerIdentifier);
				}
			}
			that.removeTesterFromActiveCallList(testerIdentifier);
			that.lowerTheHand(proctorWindow, testerIdentifier, null);

			if (that.testerImageStatuses.get(testerIdentifier) !== Constants.TesterImageStatus.EXITED) {
				that.testerImageStatuses.set(testerIdentifier, Constants.TesterImageStatus.LEFT);
				that.setStatusImage(testerIdentifier);
			}
		});

		await channel.presence.subscribe('update', async function(member) {
			if (member.data?.isProctor && member.clientId !== that.clientId) {
				const initialGroupCallDone = member.data?.initialGroupCallDone;
				if(typeof initialGroupCallDone === 'boolean') {
					that.setSharedState({
						initialGroupCallDone: initialGroupCallDone
					})
				}
			}
		});
	}

	setTextImage(testerIdentifier, textLine1, textLine2) {
		this.clearImageTimeout(testerIdentifier)
		if (this.config.testerImageHandler) {
			const messageContents = { text : CaptureImage.textToImage(textLine1, textLine2)};
			let message = RTPaaS.createMessage(Payload.createPayload(testerIdentifier, Constants.Role.TESTER, testerIdentifier, Constants.MessageNames.IMAGE, Constants.EventTypes.TESTER_IMAGE, messageContents));
			this.config.testerImageHandler(message);
		}
	}

	setStatusImage(testerIdentifier) {
		this.clearImageTimeout(testerIdentifier);
		let testerStatus = this.testerImageStatuses.get(testerIdentifier);
		let image = this.testerStatusImages.get(testerStatus);
		if (!image) {
			return;
		}
		const messageContents = { text : image };
		let message = RTPaaS.createMessage(Payload.createPayload(testerIdentifier, Constants.Role.TESTER, testerIdentifier, Constants.MessageNames.IMAGE, Constants.EventTypes.TESTER_IMAGE, messageContents));
		this.config.testerImageHandler?.(message);
	}

	handleHandHistory(testerIdentifier) {
		return this.retrieveHistory(testerIdentifier, (Constants.RetrieveHistory.DEFAULT_LIMIT_MINUTE))
			.then(history => {
				const handHistory = history.filter(msg => Constants.MessageNames.HAND === msg.data.messageName);
				return this.replayHistory(this, handHistory);
			})
			.catch(err => console.log(err))
			.finally(() => Promise.resolve());
	}

	handleLockHistory(testerIdentifier) {
		return this.retrieveHistory(testerIdentifier, Constants.VideoCapture.MAX_LOCK_STATUS_AGE_MN)
			.then(history => {
				const lockHistory = history.filter(msg => Constants.MessageNames.LOCK === msg.data.messageName);
				return this.replayHistory(this, lockHistory);
			})
			.catch(err => console.log(err))
			.finally(() => Promise.resolve());
	}
	//TODO: We might need to rework this a bit, but I think this is fine as long as the general history time limit is less than or equal to other history time limits.
	handleHistory(proctorWindow, testerIdentifier) {
		let that = this;
		//only retrieve history after opening a window for the first time
		return new Promise((resolve) => {
			this.retrieveHistory(testerIdentifier, Constants.RetrieveHistory.DEFAULT_LIMIT_MINUTE).then(
				(history) => {
					that.replayHistory(that, history).then(
						() => {}
					);
				},
				(err) => {
					console.log(err);
				}
			).finally(() => {
				resolve();
				proctorWindow.chatDiv.find('.notification').parent().remove();
				proctorWindow.windowDiv.removeClass('rtp-loading');
				proctorWindow.windowDiv.find('.rtp-loading-icon').remove();
			});
		});
	}

	handleTesterPresence(proctorWindow, testerIdentifier) {
		// Check if the tester is already in chat
		let that = this;
		let channel = this.getChannelWithHistory(testerIdentifier);
		this.isTesterPresent(channel).then(
			(isTesterPresent) => {
				if (isTesterPresent) {
					if (!that.isInVideoCall && !this.getIsInGroupAudioCall()) {
						proctorWindow.videoBtn.prop("disabled", false);
					}
					that.postNotificationMsg(`${that.getTesterFirstName(testerIdentifier)} ${that.getTesterLastName(testerIdentifier)} ${rtp_i18next.t("proctorWidget:testerIsAvailable")}`, testerIdentifier);
				} else {
					proctorWindow.videoBtn.prop("disabled", true);
					if (that.isInVideoCall) {
						that.postNotificationMsg(`${that.getTesterFirstName(testerIdentifier)} ${that.getTesterLastName(testerIdentifier)} ${rtp_i18next.t("proctorWidget:testerIsNotAvailable")}`, testerIdentifier);
					}
				}

				let isMinimized = proctorWindow.kendoWindow.options.isMinimized;
				if (!isMinimized) {
					proctorWindow.clearUnseenChatsHandler();
				}
			},
			(err) => { console.error(`Error retrieving presence data: ${err}`); }
		);
	}

	isProctor() {
		return true;
	}

	isMessageSentFromDifferentProctor(message) {
		return message.data.senderType === Constants.Role.PROCTOR && message.data.clientId !== this.clientId;
	}

	onConnection(disconnectDate) {
		let that = this;
		if (this.idAndNameMap) {
			for (let testerIdentifier of this.idAndNameMap.keys()) {
				this.setStatusImage(testerIdentifier);
				const proctorWindow = this.kendoWindows.get(testerIdentifier);
				if (proctorWindow) {
					this.handleTesterPresence(proctorWindow, testerIdentifier);
					if (disconnectDate) {
						this.retrieveHistoryFromDate(testerIdentifier, disconnectDate).then(
							(history) => {
								that.replayHistory(that, history).then(() =>{});
							},
							(err) => {
								console.error("RTPaasProctor.onConnection() ERROR "+err);
							}
						);
					}
				}
			}
		}
	}

	sendReceivedChatMessageOnly(proctorWindow, testerIdentifier, msgId, msgSeen) {
		const messageContents = { msgId : msgId, msgSeen : msgSeen };
		this.publishMessage(testerIdentifier, RTPaaS.createMessage(Payload.createPayload(this.clientId,
			Constants.Role.TESTER, testerIdentifier, Constants.MessageNames.CHAT,
			Constants.EventTypes.RECEIVED_CHAT, messageContents)));
	}

	sendReceivedChatMessage(proctorWindow, testerIdentifier, msgId) {
		let msgSeen = true;
		if ((proctorWindow && proctorWindow.unseenChatCount > 0) || document.visibilityState === 'hidden') {
			msgSeen = false;
		}
		this.sendReceivedChatMessageOnly(proctorWindow, testerIdentifier, msgId, msgSeen);
		if (!msgSeen) {
			if (!this.unseenMessagesAry) {
				this.unseenMessagesAry = [];
			}
			let messageToSee = { testerIdentifier : testerIdentifier, msgId : msgId };
			this.unseenMessagesAry.push(messageToSee);
		}
	}

	sendUnseenReceivedChatMessage(testerIdentifier) {
		if (this.unseenMessagesAry.length > 0) {
			let proctorWindow = this.kendoWindows.get(testerIdentifier);
			if (proctorWindow) {
				let newUnseenMessagesAry = [];
				for (let unseenMessage of this.unseenMessagesAry) {
					if (unseenMessage.testerIdentifier === testerIdentifier) {
						this.sendReceivedChatMessageOnly(proctorWindow, unseenMessage.testerIdentifier, unseenMessage.msgId, true);
					} else {
						newUnseenMessagesAry.push(unseenMessage);
					}
				}
				this.unseenMessagesAry = newUnseenMessagesAry;
			}
		}
	}

	// Broadcast Chat or Call functionality
	handleBroadcastMessage(message) {
		switch(message.data.eventType) {
			case Constants.EventTypes.BROADCAST_CHAT:
				let proctorWindowForBroadcast = this.kendoWindows.get(message.data.testerIdentifier);
				if (proctorWindowForBroadcast) {
					proctorWindowForBroadcast.postBroadcastMsg(message);
					if (this.isMessageSentFromDifferentProctor(message)) {
						proctorWindowForBroadcast.kendoChat.wrapper.trigger('chat-received');
					}
				}
				//no need to check if this came from another proctor because we turned "echo" off
				if (this.broadcastObj && message.data.testerIdentifier === this.broadcastChannelIdentifier) {
					this.broadcastObj.postBroadcastMsg(message);
				}
				break;
			case Constants.EventTypes.BROADCAST_CALL:
				this.activeGroupCallProctorId = message.data.clientId;
				if (this.isMessageSentFromDifferentProctor(message)) {
					this.broadcastObj.broadcastWidgetWindow.postNotificationMsg(rtp_i18next.t("proctorWidget:testMonitorInGroupCall"));
					this.setIsGroupAudioCallRunning(true);
					this.determineAllStartButtonStatuses();
				}
				break;
			case Constants.EventTypes.BROADCAST_END_CALL:
				this.activeGroupCallProctorId = null;
				if (this.isMessageSentFromDifferentProctor(message)) {
					this.setIsGroupAudioCallRunning(false);
					this.determineAllStartButtonStatuses();
				}
				break;
		}

	}

	initiateBroadcastChat(testId){
		if (this.isAblyConnected === false){
			console.warn("Cannot open Broadcast widget when Ably/Network is disconnected.");
			return;
		}

		if (this.broadcastObj){
			if (!this.broadcastObj.broadcastWidgetWindow.broadcastKendoWindow.options.visible) {
				this.broadcastObj.broadcastWidgetWindow.broadcastKendoWindow.open();
			} else if (this.broadcastObj.broadcastWidgetWindow.broadcastKendoWindow.options.isMinimized) {
				this.broadcastObj.broadcastWidgetWindow.broadcastKendoWindow.restore();
			}
		} else {
			this.broadcastObj = new Broadcast(this);
			this.broadcastChannelIdentifier = `bc_${testId}`;
			this.createChannelWithHistory(this.broadcastChannelIdentifier);
			this.subscribeToChannel(this.broadcastChannelIdentifier, Constants.MessageNames.BROADCAST, this.handleBroadcastMessage.bind(this));
			this.broadcastObj.createBroadcastWidget();
			this.setGroupCallAvailability();
		}
	}

	sendBroadcastMsgToAllStudents(text){
		for (let testerIdentifier of this.idAndNameMap.keys()) {
			let message = this.postBroadcastMsg(testerIdentifier, text);
			//since "echo" is turned off, we also need to post the messages to our own students' widgets
			let kendoWindow = this.kendoWindows.get(testerIdentifier);
			if (kendoWindow) {
				this.kendoWindows.get(testerIdentifier).postBroadcastMsg(message);
			}
		}
		// Post a message to a client channel for history
		this.postBroadcastMsg(this.broadcastChannelIdentifier, text);
	}

	postBroadcastMsg(testerIdentifier, text) {
		const messageContents = {'text' : text};
		let message = RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.BROADCAST, Constants.EventTypes.BROADCAST_CHAT, messageContents))
		this.publishMessage(testerIdentifier, message);
		return message;
	}

	retrieveBroadcastHistory() {
		return this.retrieveHistory(this.broadcastChannelIdentifier, 30);
	}

	handleGroupCallInitiated(event, data) {
		this.sendBroadcastCallToAllStudents(data);
		//We don't want to end the call before the meeting is established.
		this.broadcastObj.broadcastWidgetWindow.enableEndCallBtn();
	}

	//group audio call
	sendBroadcastCallToAllStudents(data){
		const messageContents = {'meetingId' : data.meetingId, 'broadcastChannelIdentifier' : this.broadcastChannelIdentifier};
		for (let testerIdentifier of this.idAndNameMap.keys()) {
			let message = RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.BROADCAST, Constants.EventTypes.BROADCAST_CALL, messageContents));
			this.publishMessage(testerIdentifier, message);
		}
		//publish a message informing other proctors of a group call
		let message = RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, this.broadcastChannelIdentifier, Constants.MessageNames.BROADCAST, Constants.EventTypes.BROADCAST_CALL, messageContents))
		this.publishMessage(this.broadcastChannelIdentifier, message);
	}

	handleEndGroupCall(reason){
		this.stopCallChime();
		this.setIsInGroupAudioCall(false);

		//If the group call ended due to a network issue, do not do the rest.
		if (reason && reason === Constants.EndGroupCallReason.NETWORK_ISSUE){
			return;
		}

		this.notifyEndGroupCallToAllStudents();

		//Show a dialog for unlocking testers for the first group call if the config is set to do so.
		if (this.config?.groupCallFirst === true && this.getSharedState().initialGroupCallDone === false) {
			this.setSharedState({
				initialGroupCallDone: true,
				meetingId: this.videoMonitoringMeetingId
			});
			this.publishStateToPresence(false).then(() => {
				console.log("RTPaaSProctor: Set initialGroupCallDone to true and published to presence.");
			}).catch((err) => {
				console.error("RTPaaSProctor: Error publishing initialGroupCallDone to presence: "+err);
			});
			this.broadcastObj?.broadcastWidgetWindow.showUnlockTestersDialog();
		}
	}

	notifyEndGroupCallToAllStudents(){
		this.determineAllStartButtonStatuses();
		for (let testerIdentifier of this.idAndNameMap.keys()) {
			let message = RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, testerIdentifier, Constants.MessageNames.BROADCAST, Constants.EventTypes.BROADCAST_END_CALL))
			this.publishMessage(testerIdentifier, message);
		}
		//publish a message informing other proctors that the group call ended
		let message = RTPaaS.createMessage(Payload.createPayload(this.clientId, Constants.Role.PROCTOR, this.broadcastChannelIdentifier, Constants.MessageNames.BROADCAST, Constants.EventTypes.BROADCAST_END_CALL))
		this.publishMessage(this.broadcastChannelIdentifier, message);
	}

	addTesterToActiveCallList(testerIdentifier) {
		this.testersInCall.add(testerIdentifier);
		this.setCallAndGroupCallAvailability(testerIdentifier);
	}

	removeTesterFromActiveCallList(testerIdentifier) {
		this.testersInCall.delete(testerIdentifier);
		this.setCallAndGroupCallAvailability(testerIdentifier);
	}

	cleanActiveCallList() {
		this.testersInCall.clear();
		this.setGroupCallAvailability();
	}

	//This Set is to keep track of testers that are in calls with other proctors.
	isTesterInCall(testerIdentifier) {
		return this.testersInCall.has(testerIdentifier);
	}

	toggleGroupCallButton(enabled) {
		this.broadcastObj?.broadcastWidgetWindow.groupAudioCallBtn.prop("disabled", !enabled);
	}

	//This property is used to determine if this specific proctor is in a group call
	setIsInGroupAudioCall(isInGroupAudioCall){
		this.isInGroupAudioCall = isInGroupAudioCall;
		this.setIsGroupAudioCallRunning(isInGroupAudioCall);
	}

	getIsInGroupAudioCall(){
		return Boolean(this.isInGroupAudioCall);
	}

	toString() {
		return `${this.constructor.name}=[clientId=${this.clientId}, testerIdentifiers=${this.idAndNameMap ? Array.from(this.idAndNameMap.keys()).toString() : undefined}]`;
	}

	cleanupController(){
		for (let key of this.idAndNameMap.keys()) {
			super.cleanupController(key);
			let proctorWindow = this.kendoWindows && this.kendoWindows.get(key);
			proctorWindow && proctorWindow.cleanupController();
			if (proctorWindow && proctorWindow.isVideoCallRunning()){
				proctorWindow.videoWindow.endCall(key);
			}
		}

		//Clear any disconnect timeouts
		this.disconnectTimeouts && this.disconnectTimeouts.forEach((timeoutId) => {
			clearTimeout(timeoutId);
		});
		this.disconnectTimeouts && this.disconnectTimeouts.clear();

		//Clean up Broadcast Widget
		this.broadcastObj && this.broadcastObj.cleanupController();
		this.broadcastObj = null;
		this.setIsInGroupAudioCall(false);

		//Clear overflow window elements
		this.dockManager.cleanupController();
	}
}
